import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FileText, Volume2, ShieldAlert, CheckCircle2, Clock, Trash2, Scale } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  open: { icon: Clock, label: 'Open', cls: 'bg-amber-950 border-amber-600 text-amber-300' },
  in_review: { icon: ShieldAlert, label: 'In review', cls: 'bg-cyan-950 border-cyan-600 text-cyan-300' },
  resolved: { icon: CheckCircle2, label: 'Resolved', cls: 'bg-emerald-950 border-emerald-600 text-emerald-300' },
};

const SEVERITY_STYLE = {
  low: 'bg-slate-800 border-slate-600 text-slate-300',
  medium: 'bg-amber-950 border-amber-600 text-amber-300',
  high: 'bg-rose-950 border-rose-600 text-rose-300',
};

export default function HrDashboard() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCases = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/cases`, { headers });
      setCases(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load HR cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCases(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case and all its files? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/hr/cases/${id}`, { headers });
      toast.success('Case deleted');
      fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="hr-dashboard">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-2">
              <Scale className="h-6 w-6 text-cyan-400" /> Virtual Mediator
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Log HR cases with voice + document evidence — AI produces a neutral mediation outcome.
            </p>
          </div>
          <Button onClick={() => navigate('/hr/new')} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="new-case-btn">
            <Plus className="h-4 w-4 mr-1" /> New case
          </Button>
        </div>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base">Cases ({cases.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-slate-500 py-8 text-center">Loading…</div>
            ) : cases.length === 0 ? (
              <div className="text-slate-500 py-8 text-center">
                No cases yet. Click <span className="text-cyan-400 font-semibold">New case</span> to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {cases.map(c => {
                  const st = STATUS_STYLE[c.status] || STATUS_STYLE.open;
                  const StatusIcon = st.icon;
                  return (
                    <div key={c.id}
                         className="border border-slate-700 rounded-lg p-3 hover:bg-slate-700/40 cursor-pointer transition-colors"
                         onClick={() => navigate(`/hr/${c.id}`)}
                         data-testid={`hr-case-row-${c.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-semibold truncate">{c.title}</span>
                            <Badge className={`${st.cls} text-[10px]`}>
                              <StatusIcon className="h-3 w-3 mr-1" />{st.label}
                            </Badge>
                            {c.outcome?.severity && (
                              <Badge className={`${SEVERITY_STYLE[c.outcome.severity]} text-[10px] uppercase`}>
                                {c.outcome.severity}
                              </Badge>
                            )}
                            <Badge className="bg-slate-900 border-slate-600 text-slate-400 text-[10px]">
                              {c.category}
                            </Badge>
                          </div>
                          <div className="text-slate-400 text-xs mt-1">
                            {c.involved_parties} · created {new Date(c.created_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
                            {c.created_by_name && <> by {c.created_by_name}</>}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                            <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> {c.files_count || 0} files</span>
                            {c.has_outcome && <span className="inline-flex items-center gap-1 text-emerald-400"><Volume2 className="h-3 w-3" /> analyzed</span>}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost"
                                className="text-rose-400 hover:text-rose-300 hover:bg-rose-950 h-8 w-8 p-0"
                                onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                                data-testid={`hr-case-delete-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
